import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useObservable } from '../../../../hooks/rxjs';
import { useRoomSession } from '../../../../context/room_session';
import { AppIcon, AppText, Select, styled, useTheme, ReactSelectComponents, Tooltip, UIConstants, Snackbar, useSnackbar, } from '@streem/ui-react';
import { detect } from 'detect-browser';
import { setPreferredAudioInputId } from '@streem/toolbox';
import { TooltipMessage } from './toolbar_tooltip_message';
import { useSelectedAudioDevices } from '../../../../hooks/use_selected_audio_devices';
import { useNotifyOfAudioOutputDeviceChange } from '../../../../hooks/use_notify_of_audio_output_change';
import { useNotifyOfAudioInputDeviceChange } from '../../../../hooks/use_notify_of_audio_input_change';
export const LocalAudioInputSelect = ({ isTrayOpen }) => {
    const { addNotification, snackbarNotifications, scheduleNotificationForRemoval, removeNotification, } = useSnackbar();
    const theme = useTheme();
    const roomSession = useRoomSession();
    const [showTooltip, setShowTooltip] = useState(false);
    const [localAudioEnabled] = useObservable(roomSession.media.localAudio.enabled);
    const mediaController = roomSession.media;
    const [localAudioLabel] = useObservable(roomSession.media.localAudioLabel);
    const [localDevices] = useObservable(roomSession.media.localDevices);
    const canSelectSpeaker = useMemo(() => {
        const browser = detect();
        // We can't use 'setSinkId' in Safari so we don't want to show the audio output dropdown options
        return browser?.name !== 'safari';
    }, []);
    useNotifyOfAudioOutputDeviceChange({ addNotification, removeNotification });
    useNotifyOfAudioInputDeviceChange({ addNotification, removeNotification });
    const audioDevices = useMemo(() => {
        if (Array.isArray(localDevices) && localDevices.length > 0) {
            return localDevices.filter(device => device.kind === 'audioinput' || device.kind === 'audiooutput');
        }
        return [];
    }, [localDevices]);
    const areAudioDevicesAvailable = useMemo(() => {
        return audioDevices.filter(device => device.deviceId || device.deviceId !== '').length > 0;
    }, [audioDevices]);
    const onSelectSpeaker = useCallback((device) => {
        mediaController.selectSpeaker({
            deviceId: device.deviceId,
            label: device.label,
        });
    }, [mediaController]);
    const onSelectMicrophone = useCallback((device) => {
        mediaController.selectMic({
            deviceId: device.deviceId,
            groupId: device.groupId,
            label: device.label,
        });
    }, [mediaController]);
    const { selectedSpeaker, setSelectedSpeaker, selectedMicrophone, setSelectedMicrophone } = useSelectedAudioDevices(canSelectSpeaker, onSelectMicrophone, onSelectSpeaker, audioDevices, localAudioLabel);
    useEffect(() => {
        // any time a microphone is selected, update the preferred microphone
        if (selectedMicrophone) {
            setPreferredAudioInputId(selectedMicrophone.deviceId);
        }
    }, [selectedMicrophone]);
    const show = useCallback(() => {
        if (!areAudioDevicesAvailable) {
            setShowTooltip(true);
        }
    }, [areAudioDevicesAvailable]);
    const hide = useCallback(() => setShowTooltip(false), []);
    return (_jsx(Tooltip, { showTooltip: showTooltip, message: _jsx(TooltipMessage, { text: "No Audio Devices Detected" }), distance: 20, children: _jsxs(SelectControlWrapper, { onMouseEnter: show, onMouseOver: show, onMouseLeave: hide, onMouseOut: hide, onFocus: show, onBlur: hide, children: [_jsx(Select, { isDisabled: !areAudioDevicesAvailable, isSearchable: false, menuPlacement: "top", onSelect: async (option) => {
                        if (option.kind === 'audioinput') {
                            setSelectedMicrophone(option.deviceInfo);
                            await mediaController.selectMic({
                                groupId: option.groupId,
                                deviceId: option.value,
                                label: option.label,
                            });
                            const micNotificationId = `audio-input-changed`;
                            addNotification({
                                id: micNotificationId,
                                message: `Microphone changed to ${option.label}`,
                                icon: _jsx(AppIcon, { color: theme.colors.green50, name: "DoneIcon" }),
                            });
                            scheduleNotificationForRemoval(micNotificationId, 3000);
                        }
                        if (option.kind === 'audiooutput') {
                            setSelectedSpeaker(option.deviceInfo);
                            await mediaController.selectSpeaker({
                                deviceId: option.value,
                                label: option.label,
                            });
                            const speakerNotificationId = `audio-output-changed`;
                            addNotification({
                                id: speakerNotificationId,
                                message: `Speaker changed to ${option.label}`,
                                icon: _jsx(AppIcon, { color: theme.colors.green50, name: "DoneIcon" }),
                            });
                            scheduleNotificationForRemoval(speakerNotificationId, 3000);
                        }
                    }, options: audioDevices.map((device) => ({
                        value: device.deviceId,
                        label: device.label,
                        groupId: device.groupId,
                        kind: device.kind,
                        deviceInfo: device,
                    })), components: {
                        IndicatorSeparator: () => null,
                        MenuList: ({ children, ...props }) => {
                            const audioInputChildren = children.filter((child) => child.props.data.kind === 'audioinput');
                            const audioOutputChildren = children.filter((child) => child.props.data.kind === 'audiooutput');
                            return (_jsxs(ReactSelectComponents.MenuList, { ...props, children: [_jsx(AppText, { "data-testid": "select-microphone", bold: true, style: { padding: '8px 12px' }, children: "Select a microphone" }), audioInputChildren, canSelectSpeaker && (_jsxs(_Fragment, { children: [_jsx(AppText, { "data-testid": "select-speaker", bold: true, style: { padding: '8px 12px' }, children: "Select a speaker" }), audioOutputChildren] }))] }));
                        },
                        DropdownIndicator: ({ children, ...props }) => {
                            const innerProps = Object.assign({}, props.innerProps, {
                                'data-testid': 'microphone-select-dropdown',
                            });
                            return (_jsx(ReactSelectComponents.DropdownIndicator, { ...props, innerProps: innerProps, children: children }));
                        },
                    }, styles: {
                        menu: (provided) => ({
                            ...provided,
                            width: '320px',
                            right: '8px',
                            padding: '0px',
                        }),
                        menuList: (provided) => ({
                            ...provided,
                            maxHeight: null,
                        }),
                        valueContainer: (base) => ({
                            ...base,
                            padding: '0px',
                            width: '0',
                        }),
                        control: (base, { selectProps }) => ({
                            ...base,
                            borderWidth: '0px',
                            boxShadow: 'none',
                            ':focus': {
                                borderWidth: '0px',
                            },
                            background: theme.colors.white,
                            cursor: selectProps.isDisabled ? 'not-allowed' : 'pointer',
                            pointerEvents: selectProps.isDisabled ? 'auto' : null,
                        }),
                        dropdownIndicator: (base, { selectProps }) => ({
                            ...base,
                            color: selectProps.isDisabled
                                ? theme.colors.grey30
                                : localAudioEnabled
                                    ? theme.colors.blue50
                                    : theme.colors.red50Transparent,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.5rem top 0.5rem',
                            backgroundSize: '20px 20px',
                            alignItems: 'flex-end',
                            padding: '0 0 .5rem 0',
                            transform: 'rotate(180deg)',
                            height: `${UIConstants.BOTTOM_ROW_ELEMENT_HEIGHT_REM}rem`,
                            ':hover, :active': {
                                color: selectProps.isDisabled
                                    ? theme.colors.grey30
                                    : localAudioEnabled
                                        ? theme.colors.blue50
                                        : theme.colors.red50Transparent,
                                backgroundColor: selectProps.isDisabled
                                    ? theme.colors.white
                                    : theme.colors.grey10,
                                borderRadius: '.25rem',
                            },
                        }),
                        option: (base) => ({
                            ...base,
                            backgroundColor: 'none',
                            ':hover': {
                                backgroundColor: theme.colors.blue05,
                                cursor: 'pointer',
                            },
                        }),
                    }, formatOptionLabel: (option) => {
                        const selected = (option.value === selectedMicrophone?.deviceId &&
                            option.kind === 'audioinput') ||
                            (option.value === selectedSpeaker?.deviceId &&
                                option.kind === 'audiooutput');
                        return (_jsxs(OptionContainer, { "data-testid": `${option.kind}-option-${option.label}${selected ? '-selected' : ''}`, children: [selected && (_jsx(AppIcon, { name: "DoneIcon", "data-testid": `${option.kind}-option-checkmark`, color: theme.colors.black, style: { alignSelf: 'center', flexGrow: 1 } })), _jsx(OptionText, { children: option.label })] }));
                    } }), _jsx(Snackbar, { "data-testid": "select-speaker-snackbar-message", content: snackbarNotifications, isTrayOpen: isTrayOpen, position: { vertical: 'top', horizontal: 'center' } })] }) }));
};
const SelectControlWrapper = styled.div(() => ({
    height: `${UIConstants.BOTTOM_ROW_ELEMENT_HEIGHT_REM}rem`,
}));
const OptionContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
});
const OptionText = styled(AppText)({
    textAlign: 'right',
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
});
//# sourceMappingURL=local_audio_input_select.js.map